// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';

import { getTimeFromPosition } from '@components/Timeline/Timeline/utils';

import { useTimeline } from '@components/Timeline/Timeline/TimelineProvider';

import TimelineLabelLoop from '@components/Timeline/Timeline/TimelineLabelLoop';
import TimelineAreaMarker from '@components/Timeline/Timeline/TimelineAreaMarker';
import TimelineAreaMarkerCurrent from '@components/Timeline/Timeline/TimelineAreaMarkerCurrent';
import TimelineAreaMarkerHover from '@components/Timeline/Timeline/TimelineAreaMarkerHover';

// COMPONENT ---------------------------------------------------------------- //

const TimelineAreaHeader = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const {
    headerSize,
    isCurrentDay,
    isHovering,
    markerHoverPosition,
    markerHoverTime,
    markers,
    setIsHovering,
    setMarkerHoverPosition,
    setMarkerHoverTime,
    showMarkers,
    sidebarSize,
    timelineRef,
    timezoneOverride,
    zIndex,
    zoom,
  } = useTimeline();

  const headerRef = React.useRef<HTMLDivElement>(null);

  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    const position = (timelineRef?.current?.scrollLeft || 0) - (sidebarSize || 0) + e?.clientX || 0;
    const time = getTimeFromPosition(position, zoom, timezoneOverride);

    setIsHovering(true);
    setMarkerHoverPosition(position);
    setMarkerHoverTime(time);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  if (!headerSize) return null;
  return (
    <div
      ref={headerRef}
      className={cls.timelineAreaHeader}
      style={{ zIndex: zIndex + 2, flex: `0 0 ${headerSize}px` }}
      onMouseMove={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <TimelineLabelLoop />

      {showMarkers ? (
        <>
          {isCurrentDay ? <TimelineAreaMarkerCurrent showLabel={true} /> : null}

          {markers?.map((marker, i) => (
            <TimelineAreaMarker key={`timeline-area-marker-${i}`} showLabel={true} marker={marker} />
          ))}

          {isHovering && markerHoverPosition && markerHoverTime ? <TimelineAreaMarkerHover showLabel={true} /> : null}
        </>
      ) : null}
    </div>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  timelineAreaHeader: css`
    position: sticky;
    top: 0;
    width: max-content;
    background-color: ${theme.palette.background.paper};
    cursor: col-resize;
    box-shadow: 0 0 16px #00000024;
  `,
});

// -------------------------------- EXPORT -------------------------------- //

export default TimelineAreaHeader;
